/**
 * The rule builder: when the firm chases, whom it chases, and how loudly.
 *
 * A rule is presented as a sentence rather than as a form of eight fields,
 * because that is what it is — "seven days before the deadline, email the client"
 * — and because the office needs to read the ladder at a glance to trust it. The
 * form is only shown while editing.
 *
 * The four shipped rungs are marked and cannot be deleted, only switched off.
 * That keeps "restore the standard ladder" honest: a firm that tidied up cannot
 * have lost the default it wants back.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  REMINDER_AUDIENCE_LABELS,
  REMINDER_FREQUENCY_LABELS,
  REMINDER_MODULE_LABELS,
  REMINDER_STATUS_OPTIONS,
  type ReminderAudience,
  type ReminderModule,
} from "@onecpa/shared";
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  Input,
  Panel,
  Select,
} from "@onecpa/ui";
import {
  api,
  type ReminderChannel,
  type ReminderRuleInput,
  type ReminderRuleRow,
} from "../api/client.js";
import { useRole, useSession } from "../app/session.js";

const CHANNEL_LABELS: Record<ReminderChannel, string> = {
  in_app: "באפליקציה",
  email: "דוא״ל",
  whatsapp: "וואטסאפ",
  sms: "SMS",
};

const TEMPLATE_OPTIONS = [
  { value: "monthly_material_reminder", label: "בקשת חומר חודשי" },
  { value: "monthly_material_late", label: "חומר חודשי — באיחור" },
  { value: "penalty_risk_alert", label: "התראת סיכון לקנס" },
  { value: "annual_season_open", label: "פתיחת עונת הדוחות" },
  { value: "missing_document_request", label: "בקשת מסמך חסר" },
  { value: "wealth_declaration_request", label: "בקשת הצהרת הון" },
  { value: "open_note_followup", label: "מעקב אחר הערה פתוחה" },
  { value: "ready_for_work", label: "מוכן לעבודה" },
  { value: "invoice_issued", label: "חשבונית הופקה" },
  { value: "invoice_overdue", label: "חשבונית בפיגור" },
  { value: "portal_invitation", label: "הזמנה לפורטל" },
];

const BLANK: ReminderRuleInput = {
  title: "",
  module: "monthly_reporting",
  audience: "client",
  trigger: "by_status",
  statusTrigger: "awaiting_client",
  offsetDays: -7,
  intervalDays: null,
  frequency: "one_time",
  channels: ["email"],
  messageTemplateKey: "monthly_material_reminder",
  isActive: true,
};

export function ReminderRules() {
  const session = useSession();
  const { isManager } = useRole();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ReminderRuleRow | "new" | null>(null);
  const [removing, setRemoving] = useState<ReminderRuleRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["reminder-rules", session.orgId],
    queryFn: () => api.settings.reminderRules(session.orgId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["reminder-rules"] });

  const toggle = useMutation({
    mutationFn: (rule: ReminderRuleRow) =>
      api.settings.updateReminderRule(session.orgId, rule.id, { isActive: !rule.isActive }),
    onSuccess: async () => {
      await invalidate();
      session.toast("הכלל עודכן");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const remove = useMutation({
    mutationFn: (rule: ReminderRuleRow) => api.settings.deleteReminderRule(session.orgId, rule.id),
    onSuccess: async (result) => {
      await invalidate();
      setRemoving(null);
      session.toast(result.deactivated ? "כלל ברירת המחדל כובה" : "הכלל נמחק");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const restore = useMutation({
    mutationFn: () => api.settings.restoreReminderDefaults(session.orgId),
    onSuccess: async (result) => {
      await invalidate();
      session.toast(`${result.restored} כללי ברירת מחדל שוחזרו`);
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const rules = data?.rules ?? [];
  const grouped = new Map<string, ReminderRuleRow[]>();
  for (const rule of rules) {
    const list = grouped.get(rule.module) ?? [];
    list.push(rule);
    grouped.set(rule.module, list);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-ink-muted">
          הסולם שהמערכת מגיעה איתו הוא ארבעה כללים ניתנים לעריכה, לא התנהגות קבועה.
        </p>
        {isManager && (
          <span className="ms-auto flex items-center gap-2">
            <Button
              variant="ghost"
              icon="rotate-ccw"
              loading={restore.isPending}
              onClick={() => restore.mutate()}
            >
              שחזור ברירות המחדל
            </Button>
            <Button variant="primary" icon="plus" onClick={() => setEditing("new")}>
              כלל חדש
            </Button>
          </span>
        )}
      </div>

      {rules.length === 0 && !isLoading && (
        <Panel>
          <EmptyState
            title="אין כללי תזכורת"
            description="ללא כללים המערכת לא תשלח דבר. ניתן לשחזר את ברירות המחדל."
            icon="bell"
          />
        </Panel>
      )}

      {[...grouped.entries()].map(([module, moduleRules]) => (
        <Panel
          key={module}
          title={REMINDER_MODULE_LABELS[module as ReminderModule] ?? module}
          action={<Badge tone="neutral">{moduleRules.length}</Badge>}
        >
          <ul className="divide-y divide-line-soft">
            {moduleRules.map((rule) => (
              <li
                key={rule.id}
                className="flex flex-wrap items-start gap-x-3 gap-y-1.5 px-3.5 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`text-sm font-semibold ${rule.isActive ? "text-ink" : "text-ink-faint line-through"}`}
                    >
                      {rule.title}
                    </span>
                    {rule.isSystemDefault && <Badge tone="neutral">ברירת מחדל</Badge>}
                    {!rule.isActive && <Badge tone="warning">כבוי</Badge>}
                    {rule.clientName && <Badge tone="info">{rule.clientName}</Badge>}
                  </div>
                  <p className="mt-1 text-xs text-ink-muted">{describe(rule)}</p>
                </div>

                {isManager && (
                  <span className="flex items-center gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => toggle.mutate(rule)}>
                      {rule.isActive ? "כיבוי" : "הפעלה"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(rule)}>
                      עריכה
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(rule)}>
                      מחיקה
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      ))}

      {editing && (
        <RuleEditor
          rule={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        title={removing?.isSystemDefault ? "כיבוי כלל ברירת מחדל" : "מחיקת כלל"}
        message={
          removing?.isSystemDefault
            ? "כלל שהמערכת מגיעה איתו אינו נמחק אלא מכובה, כדי שתמיד יהיה מה לשחזר."
            : `הכלל «${removing?.title ?? ""}» יימחק ולא יישלחו עוד תזכורות לפיו.`
        }
        confirmLabel={removing?.isSystemDefault ? "כיבוי" : "מחיקה"}
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}

/**
 * Who a rung is addressed to, in the dative.
 *
 * Separate from the nominative labels because Hebrew contracts the preposition
 * with the definite article: ל plus הלקוח is ללקוח, not להלקוח. Sticking a letter
 * onto the front of a display label is how a product ends up writing Hebrew that
 * reads as though a machine wrote it — which, on a screen whose whole job is to
 * be trusted at a glance, costs more than it saves.
 */
const AUDIENCE_DATIVE: Record<ReminderAudience, string> = {
  client: "ללקוח",
  bookkeeper: "למנהל/ת החשבונות",
  manager: "למנהל/ת המשרד",
  owner: "לבעל/ת המשרד",
};

/** Channels in the locative, for the same reason. */
const CHANNEL_LOCATIVE: Record<ReminderChannel, string> = {
  in_app: "באפליקציה",
  email: "בדוא״ל",
  whatsapp: "בוואטסאפ",
  sms: "ב-SMS",
};

/** `א`, then `א ו-ב`, then `א, ב ו-ג`, with the conjunction bound to the last word. */
function joinHebrew(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  const last = parts[parts.length - 1] ?? "";
  // A vav before Latin or a digit takes a hyphen; before Hebrew it does not.
  const conjunction = /^[A-Za-z0-9]/.test(last) ? `ו-${last}` : `ו${last}`;
  return `${parts.slice(0, -1).join(", ")} ${conjunction}`;
}

function days(count: number): string {
  return count === 1 ? "יום אחד" : `${count} ימים`;
}

/**
 * The rule as a sentence.
 *
 * A ladder that has to be decoded from eight columns is one nobody checks, and an
 * unchecked escalation ladder is how a client gets chased for something they
 * already sent.
 */
function describe(rule: ReminderRuleRow): string {
  const audience = AUDIENCE_DATIVE[rule.audience] ?? rule.audience;
  const channels = joinHebrew(rule.channels.map((channel) => CHANNEL_LOCATIVE[channel] ?? channel));

  const status = REMINDER_STATUS_OPTIONS[rule.module].find(
    (option) => option.value === rule.statusTrigger,
  )?.label;

  const when =
    rule.offsetDays < 0
      ? `${days(Math.abs(rule.offsetDays))} לפני המועד`
      : rule.offsetDays === 0
        ? "ביום המועד"
        : `${days(rule.offsetDays)} אחרי המועד`;

  const repeat =
    rule.trigger === "by_interval"
      ? `, וחוזר כל ${days(rule.intervalDays ?? 7)}`
      : rule.frequency !== "one_time"
        ? `, ${REMINDER_FREQUENCY_LABELS[rule.frequency] ?? rule.frequency}`
        : "";

  return `${when}${status ? ` בסטטוס «${status}»` : ""} — ${audience} ${channels}${repeat}`;
}

function RuleEditor({
  rule,
  onClose,
  onSaved,
}: {
  rule: ReminderRuleRow | null;
  onClose(): void;
  onSaved(): Promise<unknown>;
}) {
  const session = useSession();
  const [form, setForm] = useState<ReminderRuleInput>(rule ? toInput(rule) : BLANK);

  const set = <K extends keyof ReminderRuleInput>(key: K, value: ReminderRuleInput[K]): void =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = useMutation({
    mutationFn: () =>
      rule
        ? api.settings.updateReminderRule(session.orgId, rule.id, form)
        : api.settings.createReminderRule(session.orgId, form),
    onSuccess: async () => {
      await onSaved();
      session.toast(rule ? "הכלל עודכן" : "הכלל נוצר");
      onClose();
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const statuses = REMINDER_STATUS_OPTIONS[form.module] ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      title={rule ? "עריכת כלל" : "כלל תזכורת חדש"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant="primary"
            disabled={form.title.trim() === ""}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            שמירה
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="שם הכלל" required help="מה יופיע ברשימה. לדוגמה: תזכורת שבוע לפני המועד">
          {({ id }) => (
            <Input
              id={id}
              value={form.title}
              onChange={(event) => set("title", event.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-3.5 sm:grid-cols-2">
          <Field label="מודול">
            {({ id }) => (
              <Select
                id={id}
                value={form.module}
                onChange={(event) => {
                  const module = event.target.value as ReminderModule;
                  set("module", module);
                  // The old status belongs to the old module's vocabulary, so
                  // carrying it over would silently produce a rule that matches
                  // nothing.
                  set("statusTrigger", REMINDER_STATUS_OPTIONS[module][0]?.value ?? null);
                }}
              >
                {Object.entries(REMINDER_MODULE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="נמען">
            {({ id }) => (
              <Select
                id={id}
                value={form.audience}
                onChange={(event) => set("audience", event.target.value as ReminderAudience)}
              >
                {Object.entries(REMINDER_AUDIENCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="סטטוס מפעיל" help="ריק = כל סטטוס פתוח">
            {({ id }) => (
              <Select
                id={id}
                value={form.statusTrigger ?? ""}
                onChange={(event) => set("statusTrigger", event.target.value || null)}
              >
                <option value="">כל סטטוס פתוח</option>
                {statuses.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="ימים ביחס למועד" help="מספר שלילי = לפני המועד">
            {({ id }) => (
              <Input
                id={id}
                type="number"
                min={-90}
                max={365}
                value={form.offsetDays}
                onChange={(event) => set("offsetDays", Number(event.target.value))}
              />
            )}
          </Field>

          <Field label="סוג הפעלה">
            {({ id }) => (
              <Select
                id={id}
                value={form.trigger}
                onChange={(event) =>
                  set("trigger", event.target.value as ReminderRuleInput["trigger"])
                }
              >
                <option value="by_status">חד פעמי, לפי סטטוס</option>
                <option value="by_interval">חוזר, כל X ימים</option>
              </Select>
            )}
          </Field>

          {form.trigger === "by_interval" && (
            <Field label="כל כמה ימים">
              {({ id }) => (
                <Input
                  id={id}
                  type="number"
                  min={1}
                  max={365}
                  value={form.intervalDays ?? 7}
                  onChange={(event) => set("intervalDays", Number(event.target.value))}
                />
              )}
            </Field>
          )}
        </div>

        <Field label="תבנית ההודעה" help="הנוסח עצמו נערך בלשונית «תבניות»">
          {({ id }) => (
            <Select
              id={id}
              value={form.messageTemplateKey}
              onChange={(event) => set("messageTemplateKey", event.target.value)}
            >
              {TEMPLATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-xs font-semibold text-ink-muted">ערוצים</legend>
          <div className="flex flex-wrap gap-3">
            {(Object.keys(CHANNEL_LABELS) as ReminderChannel[]).map((channel) => (
              <Checkbox
                key={channel}
                label={CHANNEL_LABELS[channel]}
                checked={form.channels.includes(channel)}
                onChange={(event) =>
                  set(
                    "channels",
                    event.target.checked
                      ? [...form.channels, channel]
                      : form.channels.filter((value) => value !== channel),
                  )
                }
              />
            ))}
          </div>
          <p className="text-2xs text-ink-faint">
            בסביבה המקומית שום ערוץ אינו שולח בפועל — ההודעות נכתבות לתיקיית dev-data/outbox.
          </p>
        </fieldset>
      </div>
    </Dialog>
  );
}

function toInput(rule: ReminderRuleRow): ReminderRuleInput {
  return {
    title: rule.title,
    module: rule.module,
    audience: rule.audience,
    trigger: rule.trigger,
    statusTrigger: rule.statusTrigger,
    offsetDays: rule.offsetDays,
    intervalDays: rule.intervalDays,
    frequency: rule.frequency,
    channels: rule.channels,
    messageTemplateKey: rule.messageTemplateKey,
    clientId: rule.clientId,
    isActive: rule.isActive,
  };
}
